// @figma file=nq2oTuNpAeyLOoF0tXtO32 node=3710-7318 name="Avatar" set="shadcn-studio-pro-v5" verified=2026-03-16

import * as AvatarPrimitive from '@radix-ui/react-avatar';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { cn } from '../../lib/utils';

// ─── Root variants ─────────────────────────────────────────────────────────────

export const avatarVariants = cva('relative flex shrink-0 overflow-hidden', {
  variants: {
    /**
     * Size — Figma default is 32px (md). Additional sizes are code extensions.
     * xs → 24px · sm → 32px (Figma default) · md → 40px · lg → 48px · xl → 64px
     */
    size: {
      xs: 'size-6',
      sm: 'size-8',
      md: 'size-10',
      lg: 'size-12',
      xl: 'size-16',
    },
    /**
     * Shape — matches Figma `Shape` dimension.
     * round   → fully circular (Figma: Shape=Circle)
     * rounded → uses --radius token (Figma: Shape=Rounded)
     */
    shape: {
      round: 'rounded-full',
      rounded: 'rounded-[var(--radius)]',
    },
  },
  defaultVariants: {
    size: 'sm',
    shape: 'round',
  },
});

// ─── Indicator dot sizes (keyed to avatar size) ────────────────────────────────

const indicatorSizeMap = {
  xs: 'size-1.5',
  sm: 'size-2',
  md: 'size-2.5',
  lg: 'size-3',
  xl: 'size-3.5',
} as const;

// ─── Indicator dot colors ─────────────────────────────────────────────────────
// Figma: Online=success, Away=warning, Busy=destructive, Offline=muted-foreground

const indicatorColorMap = {
  online: 'bg-success',
  away: 'bg-warning',
  busy: 'bg-destructive',
  offline: 'bg-muted-foreground',
} as const;

// ─── Props ────────────────────────────────────────────────────────────────────

export type AvatarStatus = keyof typeof indicatorColorMap;

export interface AvatarProps
  extends React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>,
    VariantProps<typeof avatarVariants> {
  /**
   * Presence indicator dot — top-right position (matches Figma Indicator=True).
   * Online → success · Away → warning · Busy → destructive · Offline → muted
   */
  status?: AvatarStatus;
  /**
   * Ring border around the avatar (matches Figma Ring=True).
   * Adds `ring-2 ring-ring` styling.
   */
  ring?: boolean;
}

// ─── Avatar ───────────────────────────────────────────────────────────────────

const Avatar = React.forwardRef<React.ElementRef<typeof AvatarPrimitive.Root>, AvatarProps>(
  ({ className, size = 'sm', shape = 'round', status, ring, ...props }, ref) => (
    <div className="relative inline-flex shrink-0">
      <AvatarPrimitive.Root
        ref={ref}
        className={cn(avatarVariants({ size, shape }), ring && 'ring-2 ring-ring', className)}
        {...props}
      />
      {status && (
        <span
          role="img"
          aria-label={`Status: ${status}`}
          className={cn(
            // Figma: indicator is positioned top-right, slightly overlapping the avatar edge
            'absolute top-0 right-0 rounded-full border-2 border-background',
            indicatorSizeMap[size ?? 'sm'],
            indicatorColorMap[status],
          )}
        />
      )}
    </div>
  ),
);
Avatar.displayName = 'Avatar';

// ─── AvatarImage ──────────────────────────────────────────────────────────────

const AvatarImage = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Image>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Image
    ref={ref}
    className={cn('aspect-square h-full w-full object-cover', className)}
    {...props}
  />
));
AvatarImage.displayName = AvatarPrimitive.Image.displayName;

// ─── AvatarFallback ───────────────────────────────────────────────────────────

const AvatarFallback = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Fallback
    ref={ref}
    className={cn(
      'flex h-full w-full items-center justify-center bg-muted text-muted-foreground text-sm font-medium',
      className,
    )}
    {...props}
  />
));
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

// ─── AvatarGroup ──────────────────────────────────────────────────────────────
// Matches Figma "Avatar group" section (Variants=Image, Numbers, Icon)

export interface AvatarGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Maximum avatars to show before collapsing to +N overflow count */
  max?: number;
  size?: AvatarProps['size'];
  shape?: AvatarProps['shape'];
}

const AvatarGroup = React.forwardRef<HTMLDivElement, AvatarGroupProps>(
  ({ className, max = 4, size = 'sm', shape = 'round', children, ...props }, ref) => {
    const items = React.Children.toArray(children);
    const visible = max ? items.slice(0, max) : items;
    const overflow = items.length - visible.length;

    return (
      <div ref={ref} className={cn('flex items-center -space-x-3', className)} {...props}>
        {visible.map((child, i) => (
          <div
            key={i}
            className="ring-2 ring-background rounded-full"
            style={{ zIndex: visible.length - i }}
          >
            {React.isValidElement(child)
              ? React.cloneElement(child as React.ReactElement<AvatarProps>, { size, shape })
              : child}
          </div>
        ))}
        {overflow > 0 && (
          <div
            className={cn(
              'ring-2 ring-background flex items-center justify-center bg-muted text-muted-foreground font-medium',
              avatarVariants({ size, shape }),
              size === 'xs' ? 'text-[10px]' : size === 'sm' ? 'text-xs' : 'text-sm',
            )}
            role="img"
            aria-label={`${overflow} more`}
          >
            +{overflow}
          </div>
        )}
      </div>
    );
  },
);
AvatarGroup.displayName = 'AvatarGroup';

export { Avatar, AvatarFallback, AvatarGroup, AvatarImage };
